/**
 * Sticky teal brand bar for the inspector layout - logo + "Inspector
 * Tool" title, the mobile nav button, system status chips + refresh, and
 * the franchise switcher. Collapses (height 64 -> 0, opacity 1 -> 0,
 * slides up via translate) once the user has scrolled past the
 * page-header strip; restored on scroll back up. The height transition
 * drives the nav below + DataViewTable's sticky toolbar to slide up
 * in sync (they share `top` math via `--app-chrome-height`).
 *
 * Moved out of `InspectorLayout.tsx` unchanged (pure structural move) -
 * purely presentational, state arrives via props.
 */
import React from 'react'
import { Box, Container, IconButton, Stack, Typography } from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import poolwerxLogo from '../../../assets/poolwerx-logo.png'
import { APP_HEADER_HEIGHT } from '../../shared/DataViewTable/stickyOffsets'
import FranchiseSwitcher from '../../FranchiseSwitcher'
import RefreshDataButton from '../../RefreshDataButton'
import SystemStatusChips from '../../SystemStatusChips'
import { InspectorIcon } from '../../SystemIcons'
import type { FranchiseData } from '../../../hooks/useFranchiseData'
import type { User } from '../../../services/userApi'

interface InspectorBrandBarProps {
  chromeCollapsed: boolean
  isMobile: boolean
  loading: boolean
  franchiseData: FranchiseData | null
  fpCode?: string
  user?: User
  onMobileNavOpen: () => void
  onSyncStarted: () => void
}

const InspectorBrandBar: React.FC<InspectorBrandBarProps> = ({
  chromeCollapsed,
  isMobile,
  loading,
  franchiseData,
  fpCode,
  user,
  onMobileNavOpen,
  onSyncStarted,
}) => (
  <Box
    className="no-print"
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 50,
      width: '100%',
      backgroundColor: '#007f98',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderBottom: '1px solid #e0e0e0',
      overflow: 'hidden',
      height: chromeCollapsed ? 0 : `${APP_HEADER_HEIGHT}px`,
      opacity: chromeCollapsed ? 0 : 1,
      transform: chromeCollapsed ? 'translateY(-100%)' : 'translateY(0)',
      transition:
        'height 200ms ease, opacity 160ms ease, transform 200ms ease',
      pointerEvents: chromeCollapsed ? 'none' : 'auto',
    }}
  >
    <Container maxWidth="xl">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: `${APP_HEADER_HEIGHT}px`,
        }}
      >
      {/* Left Side - Poolwerx Logo and Inspector Title */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Poolwerx Logo */}
        <img
          src={poolwerxLogo}
          alt="Poolwerx"
          style={{ height: '40px' }}
        />
        <Box>
          <Typography
            variant="h6"
            component="div"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              position: 'relative',
              top: '3px',
              fontWeight: 700,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '1.1rem',
              lineHeight: 1.2
            }}
          >
            <InspectorIcon size={20} />
            <Box component="span" sx={{ position: 'relative', left: '3px', top: '1px' }}>
              Inspector Tool
            </Box>
          </Typography>
        </Box>
      </Stack>

      {/* Center - Mobile Menu Button */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open navigation"
            onClick={onMobileNavOpen}
            sx={{ color: 'white' }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Stack>

      {/* Right Side - System Status Chips, Refresh Button and Franchise Switcher */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* System Status Chips + Refresh grouped together */}
        {!loading && franchiseData && fpCode && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <SystemStatusChips
              fpCode={fpCode}
              lightspeedStoreCode={franchiseData.lightspeed_store_code}
              xeroShortCode={franchiseData.xero_short_code}
              isUSFranchise={franchiseData.country === 'USA'}
            />
            {user && (
              <RefreshDataButton
                fpCode={fpCode}
                lastSyncDate={franchiseData.last_sync_date}
                franchiseData={franchiseData}
                onSyncStarted={onSyncStarted}
              />
            )}
          </Stack>
        )}
        {/* Franchise Switcher */}
        {!loading && franchiseData && (
          <FranchiseSwitcher
            currentFpCode={fpCode}
            franchiseName={franchiseData.franchise_name}
            country={franchiseData.country}
          />
        )}
      </Stack>
      </Box>
    </Container>
  </Box>
)

export default InspectorBrandBar
